import React, { useContext, useState } from 'react';
import { Button, FormLabel, Grid, IconButton, TextField } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { map } from 'lodash';
import SettingsContext from '@/Contexts/Settings/Context';
import { arrayLength } from '@/utils/helperFunctions';
import PermissionContext from '@/Contexts/Permissions';
import { PermissionActionEnum, PermissionEntityEnum } from '@/graphql/generated/graphql';

interface IssueLevel {
  label: string;
  count: number;
  color: string;
}

const ProjectIssueLevels: React.FC = () => {
  const { projectIssueLevelValue, handleCompanySettingChange } = useContext(SettingsContext);
  const [issueLevels, setIssueLevels] = useState<IssueLevel[]>(JSON.parse(projectIssueLevelValue || '[]'));
  const { canPerformAction } = useContext(PermissionContext);
  const { Manage } = PermissionActionEnum;
  const { CompanySettings } = PermissionEntityEnum;
  const hasPermission = canPerformAction(Manage, CompanySettings);

  const handleIssueLevelChange = (key: string, index: number, value: string) => {
    const newLevels = [...issueLevels];
    newLevels[index] = { ...newLevels[index], [key]: value };
    setIssueLevels(newLevels);
  };

  const handleAddIssueLevel = () => {
    const newLevels = [...issueLevels, { label: "", count: "", color: "#000000" }];
    setIssueLevels(newLevels as IssueLevel[]);
  };

  const handleDeleteIssueLevel = (index: number) => {
    const newLevels = issueLevels.filter((_, i) => i !== index);
    setIssueLevels(newLevels);
  };

  const disableButtons = !hasPermission || issueLevels.some(level => !level.label || !level.count || !level.color)

  return (
    <React.Fragment>
      <FormLabel >Project Issue Levels</FormLabel>
      <Grid container spacing={2} mb={2} mt={1}>
        {arrayLength(issueLevels) > 0 && map((issueLevels), (level, index) => (
          <React.Fragment key={`issue-level-${index}`}>
            <Grid size={3.5}>
              <TextField
                fullWidth
                label="Label"
                value={level.label}
                onChange={(e) => handleIssueLevelChange("label", index, e.target.value)}
                error={!level.label}
                helperText={!level.label ? "Label is required" : ""}
              />
            </Grid>
            <Grid size={3.5}>
              <TextField
                fullWidth
                label="Count"
                value={level.count}
                onChange={(e) => handleIssueLevelChange("count", index, e.target.value)}
                error={!level.count}
                helperText={!level.count ? "Count is required" : ""}
              />
            </Grid>
            <Grid size={3.5}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={level.color}
                onChange={(e) => handleIssueLevelChange("color", index, e.target.value)}
              />
            </Grid>
            <Grid size={1}>
              <IconButton onClick={() => handleDeleteIssueLevel(index)} aria-label="delete">
                <DeleteIcon />
              </IconButton>
            </Grid>
          </React.Fragment>
        ))}

        <Button variant='outlined' onClick={handleAddIssueLevel} aria-label="add" color="primary" disabled={disableButtons}>
          <AddIcon />
        </Button>
        <Button
          variant='contained'
          onClick={() => handleCompanySettingChange('project-issue-level', JSON.stringify(issueLevels))}
          color="primary"
          disabled={disableButtons}
        >
          Save
        </Button>
      </Grid>
    </React.Fragment>
  );
};

export default ProjectIssueLevels;
